import {
  ArrowLeft,
  ExternalLink,
  Globe,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Phone,
  Presentation,
  Trash2,
} from "lucide-react";
import {
  EditBase,
  Form,
  email,
  required,
  useDelete,
  useEditContext,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
  type RaRecord,
  type Identifier,
} from "ra-core";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useWatch } from "react-hook-form";
import { Link } from "react-router";

import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { CancelButton } from "@/components/admin/cancel-button";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import type { Sale } from "@/components/atomic-crm/types";

import { useEsAdmin } from "../datos/useEsAdmin";
import { useFiltroAsignables } from "../datos/useFiltroAsignables";
import { useGestiona, usePuedeEditarFicha } from "../datos/useMiRol";
import { useFiltroEtapas } from "../datos/useFiltroEtapas";
import { claveOrigen, ORIGENES_ALTA } from "../dominio/catalogos";
import { enlaceLlamar, urlGoogleMaps, urlWeb } from "../dominio/contacto";
import {
  claveEmbudo,
  claveEtapa,
  esActiva,
  esAparcada,
  etapaDeVuelta,
  etapasElegibles,
  puedeTocarTarjeta,
  tienePresentacion,
} from "../dominio/etapas";
import type { Canal, Etapa, Negocio } from "../dominio/tipos";
import { BotonAbrirSdr } from "./BotonAbrirSdr";
import { CamposPorEtapa } from "./CamposPorEtapa";
import { CitasGoogleCalendar } from "./CitasGoogleCalendar";
import { CobroDialog } from "./CobroDialog";
import {
  ConsultoriaFicha,
  MarcasConsultoria,
  NoContactarFicha,
} from "./ConsultoriaFicha";
import { DatosQueFaltan } from "./DatosQueFaltan";
import { EtiquetaEtapa } from "./EtiquetaEtapa";
import { EtiquetaWeb } from "./EtiquetaWeb";
import { VigilaCambios } from "./cambiosSinGuardar";
import { MenuInteres } from "./Interes";
import { MarcaInteresado } from "./MarcaInteresado";
import { formatoFecha, formatoFechaHora, formatoImporte } from "./formato";
import { AvisoCampanaProspect } from "./AvisoCampanaProspect";
import { NotasNegocio } from "./NotasNegocio";
import { ParaLlamar } from "./ParaLlamar";
import { ProyectosFicha } from "./ProyectosFicha";
import { ResultadoSheet } from "./ResultadoSheet";
import { SelectorSector } from "./SelectorSector";
import { Semaforo } from "./Semaforo";
import { useMoverEtapa } from "./useMoverEtapa";
import { useAjustes } from "../datos/useAjustes";
import { proximaAccion } from "../dominio/agenda";
import { semaforo } from "../dominio/semaforo";
import { comoValidador, validarFicha } from "./validacion";

const nombreUsuario = (r: Sale) => `${r.first_name} ${r.last_name}`;

/**
 * Ficha de negocio estilo GHL (petición de Robert, 25/09/2026): cabecera con etapa,
 * semáforo y botones de resultado; navegación por secciones; formulario editable en dos
 * columnas; pie con Borrar / Cancelar / Guardar; historial debajo. Desde S8 (02/10/2026)
 * enseña el negocio con un proyecto en foco (el principal o el de la tarjeta pulsada), sus
 * otros proyectos y los cobros.
 */
/** La ficha está dentro de la ventana (VentanaFicha) o es una página (solo pruebas). */
const ContextoVentana = createContext<{
  enVentana: boolean;
  cerrar?: () => void;
}>({ enVentana: false });

export const FichaNegocio = ({
  id,
  proyectoId,
  enVentana = false,
  onCerrar,
}: {
  id?: Identifier;
  /** Proyecto en foco; si no, el principal (el de Ventas, o el último de Clientes). */
  proyectoId?: Identifier | null;
  enVentana?: boolean;
  onCerrar?: () => void;
} = {}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const meta = proyectoId != null ? { proyecto: proyectoId } : undefined;
  return (
    <ContextoVentana.Provider value={{ enVentana, cerrar: onCerrar }}>
      <EditBase<Negocio>
        id={id}
        resource="negocios"
        mutationMode="pessimistic"
        redirect={false}
        queryOptions={{ meta }}
        mutationOptions={{
          meta,
          onSuccess: () => {
            notify("nevora.negocios.guardada", { type: "success" });
            refresh();
          },
          onError: (error) => {
            notify(
              error instanceof Error && error.message
                ? error.message
                : "ra.notification.http_error",
              { type: "error" },
            );
          },
        }}
      >
        <Contenido />
      </EditBase>
    </ContextoVentana.Provider>
  );
};

const Contenido = () => {
  const { record, isPending } = useEditContext<Negocio>();
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { enVentana } = useContext(ContextoVentana);
  if (isPending || !record) {
    return enVentana ? (
      <p className="text-sm text-muted-foreground" role="status">
        {translate("nevora.negocios.cargando_ficha")}
      </p>
    ) : null;
  }

  if (enVentana) {
    // Rediseño y auditoría (30/10/2026): en el ordenador, dos columnas: los datos a la
    // izquierda y el historial a la derecha, a la vista sin bajar; en el móvil, uno debajo de otro.
    return (
      <div className="space-y-6">
        <Cabecera negocio={record} />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
          <div className="min-w-0 space-y-5">
            <ParaLlamar negocio={record} />
            <Formulario negocio={record} />
            {conProyectos(record) ? <ProyectosFicha negocio={record} /> : null}
          </div>
          <section
            id="historial"
            className="superficie self-start p-4 md:p-5 lg:sticky lg:top-0 lg:max-h-[calc(92vh-4rem)] lg:overflow-y-auto"
          >
            <NotasNegocio
              negocioId={record.negocio_id}
              proyectoId={record.proyecto_id}
            />
          </section>
        </div>
      </div>
    );
  }

  const cuerpo = (
    <div className="max-w-6xl space-y-5 pb-24 md:pb-8">
      <Cabecera negocio={record} />
      <ParaLlamar negocio={record} />
      <div className="grid gap-6 md:grid-cols-[200px_1fr]">
        {!isMobile ? <NavegacionSecciones /> : null}
        <div className="min-w-0 space-y-6">
          <Formulario negocio={record} />
          {conProyectos(record) ? <ProyectosFicha negocio={record} /> : null}
          <section
            id="historial"
            className="rounded-lg border bg-card p-4 md:p-5"
          >
            <NotasNegocio
              negocioId={record.negocio_id}
              proyectoId={record.proyecto_id}
            />
          </section>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <Link to="/negocios" className="flex items-center gap-2 text-sm">
            <ArrowLeft className="size-5" />
            {translate("nevora.nav.leads")}
          </Link>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return <div className="mt-1">{cuerpo}</div>;
};

/** Proyectos y cobros: cuando ya hay algo que cobrar o más de un proyecto. */
const conProyectos = (n: Negocio) =>
  (n.num_proyectos ?? 1) > 1 ||
  n.embudo === "clientes" ||
  n.etapa === "aceptada";

const SECCIONES = ["informacion", "seguimiento", "historial"] as const;

const NavegacionSecciones = () => {
  const translate = useTranslate();
  return (
    <nav className="md:sticky md:top-5 self-start space-y-0.5">
      {SECCIONES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() =>
            document
              .getElementById(s)
              ?.scrollIntoView({ behavior: "smooth", block: "start" })
          }
          className="block w-full text-left rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {translate(`nevora.ficha.secciones.${s}`)}
        </button>
      ))}
    </nav>
  );
};

/** Una celda del resumen fijo de la cabecera. */
const CeldaResumen = ({
  etiqueta,
  children,
  testId,
}: {
  etiqueta: string;
  children: ReactNode;
  testId?: string;
}) => (
  <div className="min-w-0 bg-card px-4 py-3" data-testid={testId}>
    <dt className="etiqueta">{etiqueta}</dt>
    <dd className="mt-1 flex min-h-6 items-center gap-2 text-sm">{children}</dd>
  </div>
);

/**
 * Cabecera de la ficha (rediseño y auditoría, 30/10/2026): el nombre arriba y grande; las
 * acciones a la derecha; una línea fija con etapa, responsable, último contacto y próximo
 * paso; y el resto de marcas, pequeñas, debajo.
 */
const Cabecera = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const { ajustes } = useAjustes();
  // 36: un consultor lleva Ventas como Jan y Robert en las fichas donde hace la consultoría.
  const gestiona = useGestiona()(negocio);
  // S10: un setter solo trabaja la tarjeta en sus etapas; de la consultoría en adelante, Jan y Robert.
  const tocable = puedeTocarTarjeta(negocio, gestiona);
  const [hoja, setHoja] = useState<Canal | null>(null);
  const [presentacion, setPresentacion] = useState(false);
  const accion = proximaAccion(negocio);
  const textoAccion = accion
    ? [
        accion.tipo === "siguiente_paso"
          ? accion.texto || translate("nevora.agenda.siguiente_paso")
          : translate(`nevora.agenda.${accion.tipo}`),
        accion.conHora
          ? formatoFechaHora(accion.fecha)
          : formatoFecha(accion.fecha),
      ].join(" · ")
    : null;
  return (
    <header className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 pr-10">
        <div className="min-w-0 space-y-1">
          <p className="etiqueta">
            {translate(claveEmbudo(negocio.embudo))}
            {negocio.proyecto_nombre ? ` · ${negocio.proyecto_nombre}` : ""}
          </p>
          <h1 className="titulo-pagina break-words">{negocio.nombre}</h1>
          <p className="text-sm text-muted-foreground">
            {[
              negocio.sector,
              // «Web» ya va en su etiqueta.
              negocio.origen === "web"
                ? null
                : translate(claveOrigen(negocio.origen)),
              // 43: de qué formulario y página de la web llegó.
              negocio.fuente_web,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {esAdmin && negocio.etapa === "consultoria_agendada" ? (
            <BotonAbrirSdr negocio={negocio} />
          ) : null}
          {!tocable ? (
            <p className="text-sm text-muted-foreground max-w-xs">
              {translate("nevora.ficha.la_llevan_jan_robert")}
            </p>
          ) : null}
          {tocable && gestiona && tienePresentacion(negocio) ? (
            <Button
              className="h-11 md:h-9"
              disabled={negocio.no_llamar}
              onClick={() => setPresentacion(true)}
            >
              <Presentation className="size-4 mr-1.5" />
              {translate("nevora.negocios.resultado_presentacion")}
            </Button>
          ) : null}
          {tocable ? (
            <>
              <Button
                className="h-11 md:h-9"
                variant={tienePresentacion(negocio) ? "secondary" : "default"}
                disabled={negocio.no_llamar}
                onClick={() => setHoja("llamada")}
              >
                <Phone className="size-4 mr-1.5" />
                {translate("nevora.negocios.he_llamado")}
              </Button>
              <Button
                className="h-11 md:h-9"
                variant="outline"
                disabled={negocio.no_llamar}
                onClick={() => setHoja("whatsapp")}
              >
                <MessageSquare className="size-4 mr-1.5" />
                {translate("nevora.negocios.he_escrito")}
              </Button>
              {/* Auditoría 30/10/2026: «He visitado», discreto (los setters no visitan). */}
              <Button
                className="h-11 w-11 md:h-9 md:w-9"
                variant="outline"
                size="icon"
                disabled={negocio.no_llamar}
                onClick={() => setHoja("visita")}
                aria-label={translate("nevora.negocios.he_visitado")}
                title={translate("nevora.negocios.he_visitado")}
              >
                <MapPin className="size-4" />
              </Button>
            </>
          ) : null}
          {tocable || esAdmin ? <MenuAcciones negocio={negocio} /> : null}
        </div>
      </div>

      <dl className="grid gap-px overflow-hidden rounded-xl border border-border/60 bg-border/50 shadow-tarjeta sm:grid-cols-2 lg:grid-cols-4">
        <CeldaResumen etiqueta={translate("resources.negocios.fields.etapa")}>
          <EtiquetaEtapa etapa={negocio.etapa} />
        </CeldaResumen>
        <CeldaResumen
          etiqueta={translate("nevora.negocios.responsable")}
          testId="responsable"
        >
          <span className="sr-only">
            {translate("nevora.negocios.responsable")}:{" "}
          </span>
          {negocio.asignado_a ? (
            <ReferenceField
              record={negocio as RaRecord}
              source="asignado_a"
              reference="sales"
              link={false}
            />
          ) : (
            <span className="text-muted-foreground">
              {translate("nevora.negocios.sin_asignar")}
            </span>
          )}
        </CeldaResumen>
        <CeldaResumen
          etiqueta={translate("resources.negocios.fields.ultimo_contacto")}
        >
          {semaforo(negocio, ajustes.semaforo) ? (
            <Semaforo negocio={negocio} conTexto />
          ) : (
            <span className="text-muted-foreground">
              {negocio.ultimo_contacto
                ? formatoFechaHora(negocio.ultimo_contacto)
                : translate("nevora.negocios.nunca")}
            </span>
          )}
        </CeldaResumen>
        <CeldaResumen etiqueta={translate("nevora.ficha.proximo_paso")}>
          {textoAccion ? (
            <span
              className={cn(
                "truncate",
                accion?.vencida && "font-medium text-red-700 dark:text-red-400",
              )}
              title={textoAccion}
            >
              {accion?.vencida
                ? `${translate("nevora.pipeline.vencido")} · `
                : ""}
              {textoAccion}
            </span>
          ) : esActiva(negocio.etapa) ? (
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {translate("nevora.negocios.sin_siguiente_paso")}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </CeldaResumen>
      </dl>

      <div className="flex flex-wrap items-center gap-2">
        {negocio.origen === "web" ? (
          <EtiquetaWeb fuente={negocio.fuente_web} />
        ) : null}
        <MarcaInteresado negocio={negocio} />
        <MenuInteres negocio={negocio} puedeCambiar={tocable} conTexto />
        <MarcasConsultoria negocio={negocio} />
        {negocio.no_llamar ? (
          <span className="text-xs font-semibold text-red-700 dark:text-red-400">
            {translate("nevora.negocios.no_llamar")}
          </span>
        ) : null}
        <DatosQueFaltan negocio={negocio} />
      </div>
      {esAdmin ? <AvisoCampanaProspect negocio={negocio} /> : null}
      <NoContactarFicha negocio={negocio} />
      <ConsultoriaFicha negocio={negocio} tocable={tocable} />
      <ResultadoSheet
        negocio={negocio}
        canal={hoja ?? "llamada"}
        open={hoja !== null}
        onOpenChange={(abierta) => !abierta && setHoja(null)}
      />
      <ResultadoSheet
        negocio={negocio}
        canal="videollamada"
        presentacion
        open={presentacion}
        onOpenChange={setPresentacion}
      />
    </header>
  );
};

const MenuAcciones = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { enVentana, cerrar } = useContext(ContextoVentana);
  const esAdmin = useEsAdmin();
  const gestiona = useGestiona()(negocio);
  const filtroEtapas = useFiltroEtapas();
  const [borrar] = useDelete();
  const { mover, dialogos } = useMoverEtapa();
  const [cobro, setCobro] = useState(false);

  const eliminar = () => {
    if (
      !window.confirm(
        translate("nevora.negocios.borrar_confirmacion", {
          nombre: negocio.nombre,
        }),
      )
    ) {
      return;
    }
    borrar(
      "negocios",
      { id: negocio.negocio_id, previousData: negocio },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("nevora.negocios.borrada", { type: "info" });
          if (enVentana && cerrar) {
            cerrar();
            refresh();
          } else {
            redirect("list", "negocios");
          }
        },
        // S7: la base de datos rechaza borrar una ficha con «no llamar» sin teléfono ni
        // web; S8, un cliente con cobros. Su mensaje explica qué hacer y se muestra tal cual.
        onError: (error) => {
          notify(
            error instanceof Error && error.message
              ? error.message
              : "ra.notification.http_error",
            { type: "error" },
          );
        },
      },
    );
  };

  const aparcada = esAparcada(negocio.etapa);
  const vuelta: Etapa = etapaDeVuelta(negocio.etapa_antes_de_aparcar, "nuevo");
  const tocable = puedeTocarTarjeta(negocio, gestiona);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-11 w-11 md:h-9 md:w-9"
            aria-label={translate("ra.action.open_menu")}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>
            {translate("nevora.negocios.cambiar_etapa")}
          </DropdownMenuLabel>
          {aparcada && !negocio.no_llamar && tocable ? (
            <DropdownMenuItem onSelect={() => mover(negocio, vuelta)}>
              {translate("nevora.negocios.reactivar", {
                etapa: translate(claveEtapa(vuelta)),
              })}
            </DropdownMenuItem>
          ) : null}
          {aparcada && negocio.no_llamar ? (
            <DropdownMenuLabel className="font-normal text-xs text-muted-foreground whitespace-normal">
              {translate("nevora.etapas_errores.no_llamar")}
            </DropdownMenuLabel>
          ) : null}
          {(tocable
            ? filtroEtapas(etapasElegibles(negocio.embudo, gestiona))
            : []
          )
            .filter((e) => e !== negocio.etapa)
            .map((e) => (
              <DropdownMenuItem
                key={e}
                onSelect={() => mover(negocio, e)}
                disabled={aparcada && !esAparcada(e) && negocio.no_llamar}
              >
                {translate(claveEtapa(e))}
              </DropdownMenuItem>
            ))}
          {esAdmin ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setCobro(true)}>
                {translate("nevora.negocios.registrar_cobro")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={eliminar}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4 mr-2" />
                {translate("ra.action.delete")}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogos}
      <CobroDialog
        proyecto={negocio}
        primero={negocio.embudo === "ventas"}
        open={cobro}
        onOpenChange={setCobro}
      />
    </>
  );
};

/** Validación de la ficha: fecha obligatoria si hay siguiente paso; fechas por etapa; no reactivar con «no llamar». */
const Formulario = ({ negocio }: { negocio: Negocio }) => {
  const translate = useTranslate();
  const { enVentana, cerrar } = useContext(ContextoVentana);
  const esAdmin = useEsAdmin();
  const gestiona = useGestiona()(negocio);
  const filtroAsignables = useFiltroAsignables();
  const isMobile = useIsMobile();
  // `prospect` lo pone Nevora Prospect: se enseña si es el origen, pero no se elige.
  const origenes = [
    ...ORIGENES_ALTA,
    ...(ORIGENES_ALTA.includes(negocio.origen) ? [] : [negocio.origen]),
  ].map((o) => ({
    id: o,
    name: translate(claveOrigen(o)),
    disabled: !ORIGENES_ALTA.includes(o),
  }));
  // S10: un setter elige solo sus etapas y, de la consultoría en adelante, el proyecto es
  // de solo lectura para él (la ficha sigue siendo suya: los datos de contacto sí los edita).
  const tocable = puedeTocarTarjeta(negocio, gestiona);
  // 37: quien no puede guardarla (el setter tras la consultoría, el consultor) la ve sin tocarla.
  const editable = usePuedeEditarFicha()(negocio);
  const f = (k: string) => translate(`resources.negocios.fields.${k}`);

  return (
    <Form
      key={`${negocio.updated_at ?? ""}-${negocio.proyecto_id}-${negocio.proyecto_updated_at ?? ""}`}
      record={negocio as RaRecord}
      validate={comoValidador(validarFicha(negocio))}
      className="space-y-6"
    >
      <VigilaCambios />
      {!editable ? (
        <p
          className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
          data-testid="ficha-solo-lectura"
        >
          {translate("nevora.ficha.solo_lectura")}
        </p>
      ) : null}
      <fieldset
        disabled={!editable}
        className="m-0 min-w-0 space-y-6 border-0 p-0"
      >
        <section id="informacion" className="superficie p-4 md:p-5 space-y-4">
          <h2 className="titulo-seccion">
            {translate("nevora.ficha.secciones.informacion")}
          </h2>
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            <TextInput
              source="nombre"
              validate={required()}
              helperText={false}
            />
            <CampoTelefono />
            <TextInput
              source="email"
              type="email"
              validate={email()}
              helperText={false}
            />
            <TextInput source="persona_contacto" helperText={false} />
            <TextInput source="cargo_contacto" helperText={false} />
            <BooleanInput source="es_decisor" helperText={false} />
            <CampoDireccion
              nombre={negocio.nombre}
              placeId={negocio.place_id ?? null}
            />
            <CampoWeb />
            <SelectorSector sectorActual={negocio.sector} />
          </div>
        </section>

        <section id="seguimiento" className="superficie p-4 md:p-5 space-y-4">
          <h2 className="titulo-seccion">
            {translate("nevora.ficha.secciones.seguimiento")}
          </h2>
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            {/* 47 (Robert, 31/10/2026): la etapa ya no se cambia aquí. Cambia con «Registrar
                resultado» y los botones de cada paso, o con el menú «…» de la cabecera (que pide
                el motivo al perder o aparcar): así no queda ningún cambio sin rastro. */}
            <SelectInput
              source="origen"
              choices={origenes}
              validate={required()}
              helperText={false}
              // Auditoría 30/10/2026: el origen cuenta para la comisión; tras el alta, solo dirección.
              disabled={!esAdmin}
            />
            <ReferenceInput
              source="asignado_a"
              reference="sales"
              filter={filtroAsignables}
            >
              <SelectInput
                optionText={nombreUsuario}
                helperText={false}
                emptyText={translate("nevora.negocios.sin_asignar")}
                readOnly={!esAdmin}
              />
            </ReferenceInput>
            {esAdmin ? (
              // 36: sin importes para el setter antes de «Aceptada» (los ve en «Mis comisiones»).
              <NumberInput
                source="valor_estimado"
                min={0}
                step={100}
                helperText={false}
                readOnly={!tocable}
              />
            ) : null}
            <ImporteAceptadoCampo negocio={negocio} esAdmin={esAdmin} />
            <TextInput
              source="siguiente_paso"
              helperText={false}
              readOnly={!tocable}
            />
            <DateInput
              source="fecha_siguiente_paso"
              helperText={false}
              readOnly={!tocable}
            />
            {"hora_siguiente_paso" in negocio ? (
              // 33: hora opcional; con ella, la notificación sale unos minutos antes.
              <TextInput
                source="hora_siguiente_paso"
                type="time"
                helperText={false}
                readOnly={!tocable}
                format={(v: string | null) => (v ? String(v).slice(0, 5) : "")}
                parse={(v: string) => v || null}
              />
            ) : null}
            <CamposPorEtapa readOnly={!tocable} />
            <CitasGoogleCalendar negocio={negocio} />
            <BooleanInput
              source="no_llamar"
              helperText={
                negocio.no_llamar && !esAdmin
                  ? translate("nevora.ficha.no_llamar_solo_admin")
                  : translate("nevora.ficha.no_llamar_ayuda")
              }
              readOnly={negocio.no_llamar && !esAdmin}
            />
          </div>
          <dl className="grid gap-x-6 gap-y-1 md:grid-cols-2 text-sm border-t pt-3">
            <Dato etiqueta={f("intentos")}>
              {negocio.intentos > 0
                ? translate("nevora.negocios.intentos", {
                    smart_count: negocio.intentos,
                  })
                : translate("nevora.negocios.sin_intentos")}
            </Dato>
            <Dato etiqueta={f("ultimo_contacto")}>
              {negocio.ultimo_contacto
                ? formatoFechaHora(negocio.ultimo_contacto)
                : translate("nevora.negocios.nunca")}
            </Dato>
            <Dato etiqueta={f("created_at")}>
              {formatoFecha(negocio.created_at)}
              {negocio.creado_por ? (
                <>
                  {" · "}
                  <ReferenceField
                    record={negocio as RaRecord}
                    source="creado_por"
                    reference="sales"
                    link={false}
                  />
                </>
              ) : null}
            </Dato>
            {negocio.cobrado_at ? (
              <Dato etiqueta={translate(claveEtapa("cobrado"))}>
                {formatoFecha(negocio.cobrado_at)}
              </Dato>
            ) : null}
          </dl>
        </section>
      </fieldset>

      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/95 px-4 py-3 backdrop-blur",
          enVentana
            ? "sticky bottom-0 z-10 shadow-elevada"
            : isMobile && "fixed bottom-16 left-3 right-3 z-40 shadow-lg",
        )}
      >
        <span className="text-xs text-muted-foreground hidden md:inline">
          {translate("nevora.ficha.ayuda_guardar")}
        </span>
        <div className="flex gap-2 ml-auto">
          {enVentana && cerrar ? (
            <Button type="button" variant="outline" onClick={cerrar}>
              {translate("nevora.negocios.cerrar_ficha")}
            </Button>
          ) : (
            <CancelButton />
          )}
          {editable ? <SaveButton label="ra.action.save" /> : null}
        </div>
      </div>
    </Form>
  );
};

/**
 * Importe aceptado (39, Robert 23/10/2026). Jan y Robert lo escriben, también al elegir «Aceptada»
 * en el formulario (la base no deja aceptar sin él). El setter y el consultor lo ven, sin tocarlo,
 * desde «Aceptada» (la base solo se lo da entonces).
 */
const ImporteAceptadoCampo = ({
  negocio,
  esAdmin,
}: {
  negocio: Negocio;
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  const etapa = useWatch({ name: "etapa" }) as Etapa | undefined;
  if (esAdmin) {
    const aceptandoAhora = etapa === "aceptada" && negocio.etapa !== "aceptada";
    if (
      negocio.embudo !== "clientes" &&
      negocio.etapa !== "aceptada" &&
      !aceptandoAhora
    )
      return null;
    return (
      <NumberInput
        source="importe_total"
        min={0}
        step={100}
        helperText={false}
        validate={etapa === "aceptada" ? required() : undefined}
      />
    );
  }
  if (negocio.importe_total == null) return null;
  return (
    <div className="space-y-1" data-testid="importe-aceptado">
      <p className="text-sm font-medium">
        {translate("resources.negocios.fields.importe_total")}
      </p>
      <p className="text-sm">{formatoImporte(negocio.importe_total)}</p>
    </div>
  );
};

const Dato = ({
  etiqueta,
  children,
}: {
  etiqueta: string;
  children: ReactNode;
}) => (
  <div className="flex justify-between gap-3 py-1">
    <dt className="text-muted-foreground">{etiqueta}</dt>
    <dd className="text-right font-medium">{children}</dd>
  </div>
);

const CampoTelefono = () => {
  const translate = useTranslate();
  const telefono = useWatch({ name: "telefono" }) as string | undefined;
  const tel = enlaceLlamar(telefono);
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <TextInput source="telefono" type="tel" helperText={false} />
      </div>
      {tel ? (
        <Button
          asChild
          variant="outline"
          size="icon"
          className="mb-0.5 shrink-0"
        >
          <a href={tel} aria-label={translate("nevora.negocios.llamar")}>
            <Phone className="size-4" />
          </a>
        </Button>
      ) : null}
    </div>
  );
};

const CampoDireccion = ({
  nombre,
  placeId,
}: {
  nombre: string;
  placeId: string | null;
}) => {
  const translate = useTranslate();
  const direccion = useWatch({ name: "direccion" }) as string | undefined;
  const mapa = urlGoogleMaps({
    nombre,
    direccion: direccion ?? null,
    place_id: placeId,
  });
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <TextInput source="direccion" helperText={false} />
      </div>
      {mapa ? (
        <Button
          asChild
          variant="outline"
          size="icon"
          className="mb-0.5 shrink-0"
        >
          <a
            href={mapa}
            target="_blank"
            rel="noreferrer"
            aria-label={translate("nevora.negocios.mapa")}
          >
            <ExternalLink className="size-4" />
          </a>
        </Button>
      ) : null}
    </div>
  );
};

const CampoWeb = () => {
  const translate = useTranslate();
  const web = useWatch({ name: "web" }) as string | undefined;
  const url = urlWeb(web);
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <TextInput source="web" helperText={false} />
      </div>
      {url ? (
        <Button
          asChild
          variant="outline"
          size="icon"
          className="mb-0.5 shrink-0"
        >
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            aria-label={translate("nevora.negocios.abrir_web")}
          >
            <Globe className="size-4" />
          </a>
        </Button>
      ) : null}
    </div>
  );
};
